import { useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import toast from "react-hot-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { usePayPurchaseOrderMutation } from "@/redux/services/inventory/purchaseOrder.api";
import { EXPENSE_PAYMENT_METHODS } from "@/lib/expenses";
import { displayPrice } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { PurchaseOrder } from "@/redux/services/inventory/inventory.type";
import { toastApiError } from "@/lib/apiErrors";

interface PayPurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
}

const PayPurchaseOrderModal = ({
  isOpen,
  onClose,
  purchaseOrder,
}: PayPurchaseOrderModalProps) => {
  const currency = useRestaurantCurrency();
  const { can } = usePermissions();
  const [payPurchaseOrder, { isLoading }] = usePayPurchaseOrderMutation();

  const [method, setMethod] = useState("");
  const [generateExpense, setGenerateExpense] = useState(true);
  const [paidFromDrawer, setPaidFromDrawer] = useState(false);

  const showDrawerOption =
    method === "cash" && generateExpense && can("cash_register.movement");

  const handlePay = async () => {
    if (!method) {
      toast.error("Sélectionnez un moyen de paiement");
      return;
    }
    const res = await payPurchaseOrder({
      uuid: purchaseOrder.id,
      paymentMethod: method,
      generateExpense,
      paidFromDrawer: showDrawerOption ? paidFromDrawer : false,
    });

    if ("data" in res) {
      toast.success("Bon de commande marqué comme payé");
      onClose();
    } else if ("error" in res) {
      // Refusals (already paid, no open drawer session, expense already
      // recorded…) come back as translation keys: the shared helper turns
      // them into French (SFRESTO-170).
      toastApiError(res.error, "Le paiement n'a pas pu être enregistré.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Marquer comme payé"
      size="medium"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Montant : {displayPrice(purchaseOrder.totalAmount, currency)}
        </p>

        <div>
          <label className="block text-sm font-medium text-card-foreground mb-1">
            Moyen de paiement <span className="text-danger">*</span>
          </label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {EXPENSE_PAYMENT_METHODS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={generateExpense}
            onChange={(e) => setGenerateExpense(e.target.checked)}
          />
          <span className="text-sm text-card-foreground">
            Générer une dépense
            <span className="block text-xs text-muted-foreground">
              Enregistre une dépense (catégorie Marchandises / Stock) pour le
              montant du bon.
            </span>
          </span>
        </label>

        {showDrawerOption && (
          <label className="flex items-start gap-2 cursor-pointer rounded-md border border-border p-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={paidFromDrawer}
              onChange={(e) => setPaidFromDrawer(e.target.checked)}
            />
            <span className="text-sm text-card-foreground">
              Payé depuis la caisse ?
              <span className="block text-xs text-muted-foreground">
                Une session de caisse doit être ouverte.
              </span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Retour
          </Button>
          <Button
            type="button"
            size="sm"
            isLoading={isLoading}
            onClick={handlePay}
          >
            Confirmer le paiement
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PayPurchaseOrderModal;
